import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, Check, Loader2, X } from 'lucide-react';
import { userService } from '@/services/storageService';
import aivLogo from '@/assets/aiv-logo-trans.png';
import { useI18n } from '@/contexts/I18nContext';

export default function FirstLogin() {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [acceptPrivacy, setAcceptPrivacy] = useState(false);
  const [acceptTermsAndConditions, setAcceptTermsAndConditions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fatalError, setFatalError] = useState<string | null>(null);
  const { completeFirstLogin } = useAuth();
  const navigate = useNavigate();

  const hasMinLength = password.length >= 8;
  const hasUpper = /[A-Z]/.test(password);
  const hasLower = /[a-z]/.test(password);
  const hasDigit = /[0-9]/.test(password);
  const hasSpecial = /[^A-Za-z0-9]/.test(password);
  const isPasswordValid = hasMinLength && hasUpper && hasLower && hasDigit && hasSpecial;
  const passwordsMatch = password === confirmPassword;
  const showPasswordMismatch = confirmPassword.length > 0 && !passwordsMatch;

  const passwordChecks = [
    { key: 'length', label: t('first_login_at_least_8', 'At least 8 characters'), ok: hasMinLength },
    { key: 'upper', label: t('first_login_at_least_1_upper', 'At least one uppercase letter'), ok: hasUpper },
    { key: 'lower', label: t('first_login_at_least_1_lower', 'At least one lowercase letter'), ok: hasLower },
    { key: 'digit', label: t('first_login_at_least_1_number', 'At least one number'), ok: hasDigit },
    { key: 'special', label: t('first_login_at_least_1_special', 'At least one special character'), ok: hasSpecial },
  ];

  const expiredMessage = t(
    'first_login_expired_link',
    'This first-time login link has expired. Please contact your club admin to request a new invite.',
  );

  const markExpired = (message?: string) => {
    setFatalError(message || expiredMessage);
    toast.error(message || expiredMessage);
  };

  const prevalidatedError = React.useMemo(() => {
    if (!token) {
      return t('first_login_invalid_token', 'Invalid or missing token. Please contact your administrator.');
    }
    try {
      const [, payload] = token.split('.');
      if (!payload) return null;
      const decoded = JSON.parse(atob(payload));
      const exp = decoded?.exp;
      if (typeof exp === 'number') {
        const nowSeconds = Date.now() / 1000;
        if (exp < nowSeconds) return expiredMessage;
      }
    } catch {
      // If token is not JWT, rely on backend validation.
    }
    return null;
  }, [token, expiredMessage, t]);

  React.useEffect(() => {
    if (prevalidatedError) {
      setFatalError((prev) => prev || prevalidatedError);
      toast.error(prevalidatedError);
    }
  }, [prevalidatedError]);

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const checkToken = async () => {
      try {
        await userService.validateFirstTimeToken(token);
      } catch (err: any) {
        if (cancelled) return;
        const message =
          err?.message ||
          err?.detail ||
          (err?.response?.detail as string | undefined) ||
          t('first_login_invalid_token_generic', 'Invalid token. Please contact your administrator.');
        setFatalError((prev) => prev || message);
        toast.error(message);
      }
    };
    void checkToken();
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('first_login_password_mismatch', 'Passwords do not match'));
      return;
    }

    if (!isPasswordValid) {
      toast.error(t('first_login_password_requirements', 'Password must meet all password requirements'));
      return;
    }

    if (!acceptPrivacy || !acceptTermsAndConditions) {
      toast.error(
        t('first_login_accept_required', 'Please agree to the privacy policy and terms and conditions to continue'),
      );
      return;
    }

    setIsLoading(true);
    try {
      const result = await completeFirstLogin(token, password, acceptPrivacy, acceptTermsAndConditions);
      if (result.success) {
        toast.success(t('first_login_password_set_success', 'Password set successfully! Please log in.'));
        navigate('/login');
      } else {
        const message = result.error || t('first_login_password_set_failed', 'Failed to set password');
        if (message.toLowerCase().includes('expire')) {
          markExpired(message);
          return;
        }
        toast.error(message);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : t('common_error_occurred', 'An error occurred');
      if (message.toLowerCase().includes('expire')) {
        markExpired(message);
      } else {
        toast.error(message);
      }
    } finally {
      setIsLoading(false);
    }
  };

  const blockingError = fatalError || prevalidatedError;

  if (!token || blockingError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <p className="text-destructive">
              {blockingError || t('first_login_invalid_token', 'Invalid or missing token. Please contact your administrator.')}
            </p>
            <Button onClick={() => navigate('/login')} className="mt-4">
              {t('first_login_go_login', 'Go to Login')}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/10 via-background to-accent/10 p-4">
      <div className="w-full max-w-md md:max-w-3xl lg:max-w-4xl animate-fade-in">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center rounded-2xl text-primary-foreground mb-4">
            <img style={{ height: '130px', width: '130px' }} src={aivLogo} alt="AIV Logo" className="object-contain" />
          </div>
          <h1 className="text-4xl md:text-5xl font-display font-bold text-foreground">SmashLytics</h1>
          <p className="text-muted-foreground mt-2 text-sm md:text-lg">
            {t('first_login_setup_subtitle', 'Set up your password to get started')}
          </p>
        </div>

        <Card className="border-border/50 shadow-xl">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl md:text-4xl font-display">{t('first_login_title', 'Create Your Password')}</CardTitle>
            <CardDescription className="text-sm md:text-lg">{t('first_login_desc', 'Choose a secure password')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4 md:space-y-6">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-base md:text-lg">
                  {t('first_login_new_password', 'New Password')}
                </Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="********"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="h-11 md:h-14 text-base md:text-lg pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
                <div className="rounded-md border border-border/70 bg-muted/30 p-3">
                  <ul className="space-y-1.5">
                    {passwordChecks.map((rule) => (
                      <li key={rule.key} className="flex items-center gap-2 text-sm md:text-base">
                        {rule.ok ? <Check size={14} className="text-success shrink-0" /> : <X size={14} className="text-muted-foreground shrink-0" />}
                        <span className={rule.ok ? 'text-foreground' : 'text-muted-foreground'}>{rule.label}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-base md:text-lg">
                  {t('first_login_confirm_password', 'Confirm Password')}
                </Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  placeholder="********"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-11 md:h-14 text-base md:text-lg"
                />
                {showPasswordMismatch && (
                  <p className="text-sm text-destructive">{t('first_login_password_mismatch', 'Passwords do not match')}</p>
                )}
              </div>

              <div className="space-y-3 rounded-lg border border-dashed border-border/60 bg-muted/40 p-4">
                <Label className="flex items-center gap-3 text-sm md:text-base font-medium leading-tight">
                  <Checkbox
                    id="privacy"
                    checked={acceptPrivacy}
                    onCheckedChange={(value) => setAcceptPrivacy(!!value)}
                    className="h-6 w-6 shrink-0"
                  />
                  <span className="text-muted-foreground">
                    {t('first_login_agree_privacy', 'I agree to the')}{' '}
                    <Link to="/privacy-policy" className="text-primary hover:underline">
                      {t('privacy_title', 'Privacy Policy')}
                    </Link>
                    .
                  </span>
                </Label>
                <Label className="flex items-center gap-3 text-sm md:text-base font-medium leading-tight">
                  <Checkbox
                    id="terms-and-conditions"
                    checked={acceptTermsAndConditions}
                    onCheckedChange={(value) => setAcceptTermsAndConditions(!!value)}
                    className="h-6 w-6 shrink-0"
                  />
                  <span className="text-muted-foreground">
                    {t('first_login_agree_terms', 'I agree to the')}{' '}
                    <Link to="/terms-and-conditions" className="text-primary hover:underline">
                      {t('terms_title', 'Terms and Conditions')}
                    </Link>
                    .
                  </span>
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full h-11 md:h-14 text-base md:text-lg"
                disabled={isLoading || !acceptPrivacy || !acceptTermsAndConditions || !isPasswordValid || !passwordsMatch}
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" /> {t('first_login_setting_password', 'Setting password...')}
                  </span>
                ) : (
                  t('first_login_set_continue', 'Set Password & Continue')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
